import { Link } from "react-router-dom";
import { Button } from "@/components/ui";
import PoweredByDLSoft from "@/components/branding/PoweredByDLSoft";
import {
  Users,
  Award,
  Calendar,
  BarChart3,
  Shield,
  Zap,
  CheckCircle,
  ArrowRight,
  Heart,
  Trophy,
  Target,
  Sparkles,
  Menu,
  X,
  Smartphone,
  Monitor,
  Building2,
  Church,
  UserCheck,
  ClipboardCheck,
  DollarSign,
  FileText,
  Layers,
  ChevronDown,
  Stamp,
  TrendingUp,
  Settings,
  Bell,
  MapPin,
} from "lucide-react";
import { useState } from "react";

// ============================================
// NAVBAR
// ============================================
function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setMobileMenuOpen(false);
    }
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            <img
              src="/branding/insignia_icon.svg"
              alt="Insignia"
              className="w-10 h-10"
            />
            <span className="text-xl font-bold bg-gradient-to-r from-primary-600 to-accent-600 bg-clip-text text-transparent">
              Insignia
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            <button
              onClick={() => scrollToSection("problema")}
              className="text-gray-600 hover:text-primary-600 transition-colors font-medium"
            >
              ¿Por qué Insignia?
            </button>
            <button
              onClick={() => scrollToSection("solucion")}
              className="text-gray-600 hover:text-primary-600 transition-colors font-medium"
            >
              Cómo funciona
            </button>
            <button
              onClick={() => scrollToSection("plataformas")}
              className="text-gray-600 hover:text-primary-600 transition-colors font-medium"
            >
              Lo que ofrece
            </button>
            <button
              onClick={() => scrollToSection("arquitectura")}
              className="text-gray-600 hover:text-primary-600 transition-colors font-medium"
            >
              Para quién es
            </button>
            <Link
              to="/guia"
              className="text-primary-600 hover:text-primary-700 transition-colors font-semibold"
            >
              Guía paso a paso
            </Link>
          </div>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-3">
            <Link to="/login">
              <Button variant="ghost" size="md">
                Iniciar sesión
              </Button>
            </Link>
            <Link to="/register">
              <Button size="md">
                Acceder al sistema
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 rounded-lg text-gray-600 hover:bg-gray-100"
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 animate-fade-in-down">
          <div className="px-4 py-4 space-y-3">
            <button
              onClick={() => scrollToSection("problema")}
              className="block w-full text-left py-2 text-gray-600 hover:text-primary-600 font-medium"
            >
              El Problema
            </button>
            <button
              onClick={() => scrollToSection("solucion")}
              className="block w-full text-left py-2 text-gray-600 hover:text-primary-600 font-medium"
            >
              Solución
            </button>
            <button
              onClick={() => scrollToSection("plataformas")}
              className="block w-full text-left py-2 text-gray-600 hover:text-primary-600 font-medium"
            >
              Plataformas
            </button>
            <button
              onClick={() => scrollToSection("arquitectura")}
              className="block w-full text-left py-2 text-gray-600 hover:text-primary-600 font-medium"
            >
              Arquitectura
            </button>
            <Link
              to="/guia"
              onClick={() => setMobileMenuOpen(false)}
              className="block w-full text-left py-2 text-primary-600 hover:text-primary-700 font-semibold"
            >
              Guía paso a paso
            </Link>
            <div className="pt-4 flex flex-col gap-3">
              <Link to="/login">
                <Button variant="secondary" className="w-full">
                  Iniciar sesión
                </Button>
              </Link>
              <Link to="/register">
                <Button className="w-full">
                  Acceder al sistema
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}

// ============================================
// HERO SECTION
// ============================================
function HeroSection() {
  return (
    <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary-50 via-white to-accent-50" />
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-radial from-primary-200/40 to-transparent rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-gradient-radial from-accent-200/30 to-transparent rounded-full blur-3xl translate-y-1/2 -translate-x-1/3" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-100 text-primary-700 text-sm font-medium mb-6 animate-fade-in-up">
            <Sparkles className="w-4 h-4" />
            Plataforma oficial de la Asociación del Oriente Colombiano
          </div>

          <h1
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight animate-fade-in-up"
            style={{ animationDelay: "100ms" }}
          >
            Gestión digital para{" "}
            <span className="text-aventureros-700">Aventureros</span>,{" "}
            <span className="text-conquistadores-700">Conquistadores</span> y{" "}
            <span className="text-guias-700">Guías Mayores</span>
          </h1>

          <p
            className="mt-6 text-lg md:text-xl text-gray-600 leading-relaxed animate-fade-in-up max-w-3xl mx-auto"
            style={{ animationDelay: "200ms" }}
          >
            <strong>Insignia</strong> es la plataforma integral que digitaliza
            la administración de tu club. Portal web para directivos y
            aplicación móvil para miembros, unificando gestión de membresías,
            clases progresivas, especialidades, eventos, asistencia y finanzas.
          </p>

          <div
            className="mt-10 flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up"
            style={{ animationDelay: "300ms" }}
          >
            <Link to="/login">
              <Button
                size="xl"
                className="w-full sm:w-auto shadow-lg shadow-primary-500/30"
              >
                Acceder al sistema
                <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
            <a href="#plataformas">
              <Button
                variant="secondary"
                size="xl"
                className="w-full sm:w-auto"
              >
                Conocer más
                <ChevronDown className="w-5 h-5" />
              </Button>
            </a>
          </div>

          {/* Club Types badges */}
          <div
            className="mt-12 flex flex-wrap justify-center gap-4 animate-fade-in-up"
            style={{ animationDelay: "400ms" }}
          >
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-aventureros-100 text-aventureros-700 font-medium">
              <Heart className="w-4 h-4" />
              Aventureros (6-9 años)
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-conquistadores-100 text-conquistadores-700 font-medium">
              <Shield className="w-4 h-4" />
              Conquistadores (10-15 años)
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-guias-100 text-guias-700 font-medium">
              <Award className="w-4 h-4" />
              Guías Mayores (16+ años)
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================
// PROBLEM SECTION
// ============================================
function ProblemSection() {
  const problems = [
    {
      icon: FileText,
      title: "Registros en papel",
      description:
        "Listas de asistencia, fichas de matrícula y registros de progreso se pierden o deterioran. No hay centralización de la información.",
    },
    {
      icon: Users,
      title: "Comunicación fragmentada",
      description:
        "Grupos de WhatsApp, llamadas individuales, notas de voz. La información importante se pierde entre cientos de mensajes.",
    },
    {
      icon: Award,
      title: "Seguimiento manual de clases",
      description:
        "El avance en clases progresivas y especialidades se registra en cuadernos. Difícil saber quién completó qué requisitos.",
    },
    {
      icon: DollarSign,
      title: "Control financiero básico",
      description:
        "Cuotas, campamentos y actividades se manejan con cuadernos o Excel. Difícil saber quién debe y generar reportes.",
    },
    {
      icon: Calendar,
      title: "Planificación desorganizada",
      description:
        "Calendarios físicos o mentales. Los padres no tienen visibilidad de actividades y los directivos pierden la trazabilidad.",
    },
    {
      icon: BarChart3,
      title: "Sin métricas ni reportes",
      description:
        "¿Cuántos miembros activos hay? ¿Cuál es el promedio de asistencia? ¿Cuánto recaudamos? Preguntas difíciles de responder.",
    },
  ];

  return (
    <section
      id="problema"
      className="py-20 md:py-32 bg-gradient-to-b from-gray-50 to-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-100 text-red-700 text-sm font-medium mb-4">
            <Target className="w-4 h-4" />
            ¿Por qué Insignia?
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
            Llevar un club juvenil ya no tiene que ser tan complicado
          </h2>
          <p className="mt-4 text-lg text-gray-600">
            Cada semana los directores y consejeros pierden horas entre
            cuadernos, hojas de Excel y mensajes de WhatsApp. Insignia organiza
            todo en un solo lugar para que puedan dedicar su tiempo a lo que de
            verdad importa: los niños y jóvenes del club.
          </p>
        </div>

        {/* Problems grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {problems.map((problem, index) => {
            const Icon = problem.icon;
            return (
              <div
                key={index}
                className="bg-white rounded-2xl p-6 border border-red-100 hover:border-red-200 hover:shadow-lg transition-all duration-300"
              >
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-red-100 mb-4">
                  <Icon className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {problem.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {problem.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ============================================
// SOLUTION SECTION
// ============================================
function SolutionSection() {
  return (
    <section id="solucion" className="py-20 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-100 text-emerald-700 text-sm font-medium mb-4">
            <CheckCircle className="w-4 h-4" />
            Cómo funciona
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
            Una herramienta para directivos, otra para las familias
          </h2>
          <p className="mt-4 text-lg text-gray-600">
            Insignia tiene dos partes que trabajan juntas: una página web para
            directores y consejeros, y una app móvil para que cada miembro vea
            su progreso. Lo que pasa en una se ve al instante en la otra.
          </p>
        </div>

        {/* Two platforms */}
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Web Portal */}
          <div className="bg-gradient-to-br from-primary-50 to-primary-100/50 rounded-3xl p-8 border border-primary-200">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center shadow-lg shadow-primary-500/30">
                <Monitor className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900">Portal Web</h3>
                <p className="text-primary-600 font-medium">
                  Para directores, consejeros y administradores
                </p>
              </div>
            </div>
            <p className="text-gray-600 mb-6">
              Desde cualquier computador o tablet, lleva el control completo de
              tu club: a quién inscribiste, quién vino a la reunión, cuánto se
              ha recaudado y qué viene en la agenda del mes.
            </p>
            <ul className="space-y-3">
              {[
                "Lista completa de miembros y unidades",
                "Registro de clases, especialidades e investiduras",
                "Control de asistencia a reuniones",
                "Cobros, pagos y gastos del club",
                "Planeación de campamentos y eventos",
                "Reportes listos para imprimir o enviar",
                "Permisos diferenciados por rol",
              ].map((feature, i) => (
                <li key={i} className="flex items-center gap-3 text-gray-700">
                  <CheckCircle className="w-5 h-5 text-primary-600 flex-shrink-0" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Mobile App */}
          <div className="bg-gradient-to-br from-accent-50 to-accent-100/50 rounded-3xl p-8 border border-accent-200">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-accent-500 to-accent-600 flex items-center justify-center shadow-lg shadow-accent-500/30">
                <Smartphone className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900">App Móvil</h3>
                <p className="text-accent-600 font-medium">
                  Para miembros, padres y familias
                </p>
              </div>
            </div>
            <p className="text-gray-600 mb-6">
              Disponible para celular Android e iPhone. Cada miembro tiene su
              propio carnet digital y puede ver en cualquier momento sus
              especialidades, sus eventos y sus próximos campamentos.
            </p>
            <ul className="space-y-3">
              {[
                "Carnet digital con código QR",
                "Progreso de clases y especialidades",
                "Línea de tiempo de campamentos y eventos",
                "Catálogo de especialidades y maestrías",
                "Estadísticas de su trayectoria en el club",
                "Avisos de eventos y recordatorios de pago",
                "Perfil personal con foto y datos de contacto",
              ].map((feature, i) => (
                <li key={i} className="flex items-center gap-3 text-gray-700">
                  <CheckCircle className="w-5 h-5 text-accent-600 flex-shrink-0" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================
// WEB FEATURES SECTION
// ============================================
function WebFeaturesSection() {
  const modules = [
    {
      icon: Building2,
      title: "Organización",
      description:
        "Estructura jerárquica: Unión → Asociación → Iglesia → Club. Cada nivel con permisos específicos.",
      color: "primary",
    },
    {
      icon: Users,
      title: "Miembros",
      description:
        "Ficha completa por miembro: datos personales, contacto de emergencia, información médica, unidad asignada.",
      color: "conquistadores",
    },
    {
      icon: Award,
      title: "Progreso",
      description:
        "Registro de clases progresivas, especialidades obtenidas, maestrías e investiduras con fechas y evidencias.",
      color: "accent",
    },
    {
      icon: Calendar,
      title: "Eventos",
      description:
        "Campamentos, caminatas, reuniones especiales. Control de inscripción, confirmación y asistencia por evento.",
      color: "aventureros",
    },
    {
      icon: ClipboardCheck,
      title: "Asistencia",
      description:
        "Control de reuniones regulares. Registro de asistencia por fecha, estadísticas de participación.",
      color: "guias",
    },
    {
      icon: DollarSign,
      title: "Finanzas",
      description:
        "Cobros mensuales, pagos de eventos, registro de gastos. Estado de cuenta por miembro.",
      color: "primary",
    },
    {
      icon: FileText,
      title: "Reportes",
      description:
        "Informes de membresía, progreso académico, asistencia y finanzas. Exportables a PDF.",
      color: "conquistadores",
    },
    {
      icon: Settings,
      title: "Administración",
      description:
        "Gestión de usuarios del sistema, asignación de roles y permisos por módulo.",
      color: "accent",
    },
  ];

  const colorClasses: Record<string, { bg: string; icon: string }> = {
    primary: { bg: "bg-primary-100", icon: "text-primary-600" },
    accent: { bg: "bg-accent-100", icon: "text-accent-600" },
    aventureros: { bg: "bg-aventureros-100", icon: "text-aventureros-600" },
    conquistadores: {
      bg: "bg-conquistadores-100",
      icon: "text-conquistadores-600",
    },
    guias: { bg: "bg-guias-100", icon: "text-guias-600" },
  };

  return (
    <section
      id="plataformas"
      className="py-20 md:py-32 bg-gradient-to-b from-gray-50 to-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-100 text-primary-700 text-sm font-medium mb-4">
            <Monitor className="w-4 h-4" />
            Lo que ofrece
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
            Todo lo que tu club necesita, en un solo lugar
          </h2>
          <p className="mt-4 text-lg text-gray-600">
            Cada sección está pensada para una tarea real del día a día: apuntar
            quien vino a la reunión, registrar una especialidad, cobrar la
            mensualidad o planear el próximo campamento.
          </p>
        </div>

        {/* Modules grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {modules.map((module, index) => {
            const Icon = module.icon;
            const colors = colorClasses[module.color];
            return (
              <div
                key={index}
                className="bg-white rounded-2xl p-6 border border-gray-100 hover:border-gray-200 hover:shadow-xl hover:shadow-gray-100/50 transition-all duration-300"
              >
                <div
                  className={`inline-flex items-center justify-center w-12 h-12 rounded-xl ${colors.bg} mb-4`}
                >
                  <Icon className={`w-6 h-6 ${colors.icon}`} />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {module.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {module.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ============================================
// MOBILE FEATURES SECTION
// ============================================
function MobileFeaturesSection() {
  return (
    <section className="py-20 md:py-32 bg-gradient-to-br from-accent-600 via-accent-700 to-primary-700 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0 bg-mesh opacity-20" />
      <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-primary-500/20 rounded-full blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 text-white text-sm font-medium mb-6">
              <Smartphone className="w-4 h-4" />
              Para los miembros y sus familias
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              El carnet digital que cada miembro lleva en el bolsillo
            </h2>
            <p className="text-lg text-white mb-8">
              En la app móvil cada Aventurero, Conquistador o Guía Mayor tiene
              su propio espacio: ve sus especialidades conseguidas, los eventos
              a los que ha ido y los campamentos que vienen, todo en un diseño
              sencillo y bonito.
            </p>

            <div className="space-y-4">
              {[
                {
                  icon: UserCheck,
                  title: "Carnet con código QR",
                  description:
                    "Verificación de membresía en eventos y campamentos.",
                },
                {
                  icon: Trophy,
                  title: "Galería de logros",
                  description:
                    "Visualización de especialidades, maestrías y clases completadas.",
                },
                {
                  icon: Stamp,
                  title: "Sellos de eventos",
                  description:
                    "Cada campamento y evento especial se registra como un sello coleccionable.",
                },
                {
                  icon: TrendingUp,
                  title: "Estadísticas personales",
                  description:
                    "Años en el club, eventos asistidos, especialidades por categoría.",
                },
                {
                  icon: Bell,
                  title: "Notificaciones",
                  description:
                    "Recordatorios de eventos, pagos pendientes y novedades del club.",
                },
              ].map((feature, i) => {
                const Icon = feature.icon;
                return (
                  <div
                    key={i}
                    className="flex items-start gap-4 bg-white/10 backdrop-blur-sm rounded-xl p-4"
                  >
                    <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-white">
                        {feature.title}
                      </h4>
                      <p className="text-white/70 text-sm">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Phone mockup placeholder */}
          <div className="flex justify-center">
            <div className="relative">
              {/* Phone frame */}
              <div className="w-72 h-[580px] bg-gray-900 rounded-[3rem] p-3 shadow-2xl">
                <div className="w-full h-full bg-gradient-to-b from-primary-100 to-white rounded-[2.5rem] overflow-hidden relative">
                  {/* Status bar */}
                  <div className="h-12 bg-primary-600 flex items-center justify-center">
                    <div className="w-24 h-6 bg-black rounded-full" />
                  </div>

                  {/* Passport preview content */}
                  <div className="p-4 space-y-4">
                    {/* Header */}
                    <div className="text-center">
                      <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center mb-2">
                        <span className="text-3xl text-white font-bold">
                          JD
                        </span>
                      </div>
                      <h4 className="font-bold text-gray-900">Juan Diego</h4>
                      <p className="text-sm text-gray-500">Conquistador</p>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div className="bg-primary-50 rounded-xl p-2">
                        <div className="text-lg font-bold text-primary-600">
                          5
                        </div>
                        <div className="text-xs text-gray-500">Años</div>
                      </div>
                      <div className="bg-accent-50 rounded-xl p-2">
                        <div className="text-lg font-bold text-accent-600">
                          24
                        </div>
                        <div className="text-xs text-gray-500">
                          Especialidades
                        </div>
                      </div>
                      <div className="bg-aventureros-50 rounded-xl p-2">
                        <div className="text-lg font-bold text-aventureros-600">
                          12
                        </div>
                        <div className="text-xs text-gray-500">Eventos</div>
                      </div>
                    </div>

                    {/* Class progress */}
                    <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-100">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">
                          Clase Guía
                        </span>
                        <span className="text-xs text-primary-600 font-semibold">
                          85%
                        </span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full w-[85%] bg-gradient-to-r from-primary-500 to-primary-600 rounded-full" />
                      </div>
                    </div>

                    {/* Recent achievement */}
                    <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-100">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                          <Award className="w-6 h-6 text-amber-600" />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            Natación I
                          </div>
                          <div className="text-xs text-gray-500">
                            Última especialidad
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating badges */}
              <div className="absolute -top-4 -left-8 bg-white rounded-xl shadow-xl p-3 animate-float">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <CheckCircle className="w-4 h-4 text-emerald-600" />
                  </div>
                  <span className="text-sm font-medium text-gray-900">
                    Investido
                  </span>
                </div>
              </div>

              <div
                className="absolute -bottom-4 -right-8 bg-white rounded-xl shadow-xl p-3 animate-float"
                style={{ animationDelay: "1s" }}
              >
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center">
                    <Stamp className="w-4 h-4 text-primary-600" />
                  </div>
                  <span className="text-sm font-medium text-gray-900">
                    Camporee 2025
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================
// ARCHITECTURE SECTION
// ============================================
function ArchitectureSection() {
  return (
    <section id="arquitectura" className="py-20 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gray-100 text-gray-700 text-sm font-medium mb-4">
            <Layers className="w-4 h-4" />
            Para quién es
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
            Pensado para cada nivel de la organización
          </h2>
          <p className="mt-4 text-lg text-gray-700">
            Insignia respeta la estructura natural de la iglesia: cada persona
            ve solo lo que le corresponde, con la información y los permisos
            adecuados a su rol.
          </p>
        </div>

        {/* Hierarchy diagram */}
        <div className="max-w-4xl mx-auto">
          <div className="space-y-6">
            {/* Union Level */}
            <div className="bg-gradient-to-r from-primary-700 to-primary-800 rounded-2xl p-6 text-white shadow-lg">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white/30 rounded-xl flex items-center justify-center">
                  <Building2 className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Unión</h3>
                  <p className="text-white font-medium">
                    Vista general de todas las asociaciones y clubes para
                    consolidar reportes a nivel nacional.
                  </p>
                </div>
              </div>
            </div>

            {/* Arrow */}
            <div className="flex justify-center">
              <ChevronDown className="w-8 h-8 text-gray-300" />
            </div>

            {/* Association Level */}
            <div className="bg-gradient-to-r from-conquistadores-700 to-conquistadores-800 rounded-2xl p-6 text-white shadow-lg">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white/30 rounded-xl flex items-center justify-center">
                  <MapPin className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Asociación</h3>
                  <p className="text-white font-medium">
                    Acompaña a las iglesias y clubes de su territorio, organiza
                    camporees y eventos regionales.
                  </p>
                </div>
              </div>
            </div>

            {/* Arrow */}
            <div className="flex justify-center">
              <ChevronDown className="w-8 h-8 text-gray-300" />
            </div>

            {/* Church Level */}
            <div className="bg-gradient-to-r from-aventureros-700 to-aventureros-800 rounded-2xl p-6 text-white shadow-lg">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white/30 rounded-xl flex items-center justify-center">
                  <Church className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">
                    Iglesia local
                  </h3>
                  <p className="text-white font-medium">
                    Cada iglesia puede tener uno o varios clubes: Aventureros,
                    Conquistadores y Guías Mayores.
                  </p>
                </div>
              </div>
            </div>

            {/* Arrow */}
            <div className="flex justify-center">
              <ChevronDown className="w-8 h-8 text-gray-300" />
            </div>

            {/* Club Level */}
            <div className="bg-gradient-to-r from-guias-700 to-guias-800 rounded-2xl p-6 text-white shadow-lg">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white/30 rounded-xl flex items-center justify-center">
                  <Shield className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Club</h3>
                  <p className="text-white font-medium">
                    El equipo del club — director, consejeros y secretaría —
                    gestiona el día a día: miembros, asistencia, progreso y
                    finanzas.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Roles explanation */}
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            <div className="bg-gray-50 rounded-xl p-5 border border-gray-100">
              <h4 className="font-semibold text-gray-900 mb-2">
                Director de club
              </h4>
              <p className="text-sm text-gray-700">
                Administra todo lo de su club: miembros, progreso, eventos,
                asistencia, finanzas y reportes.
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-5 border border-gray-100">
              <h4 className="font-semibold text-gray-900 mb-2">
                Consejero de unidad
              </h4>
              <p className="text-sm text-gray-700">
                Lleva los registros de su unidad: pasa lista, registra clases y
                acompaña el progreso de sus chicos.
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-5 border border-gray-100">
              <h4 className="font-semibold text-gray-900 mb-2">
                Miembros y familias
              </h4>
              <p className="text-sm text-gray-700">
                Desde la app móvil ven el carnet, el progreso del miembro, los
                eventos próximos y los pagos pendientes.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================
// TRUST / SAFETY SECTION
// ============================================
function TrustSection() {
  const pillars = [
    {
      icon: Shield,
      title: "Tus datos están protegidos",
      description:
        "Información cifrada y guardada en servidores seguros. Solo las personas autorizadas de tu club pueden ver lo que les corresponde.",
    },
    {
      icon: Heart,
      title: "Pensado para los niños y jóvenes",
      description:
        "Como manejamos datos de menores, cumplimos con la Ley 1581 de 2012 (Habeas Data) y pedimos autorización a padres y representantes.",
    },
    {
      icon: CheckCircle,
      title: "Sin costo para los clubes",
      description:
        "Insignia es un proyecto sin ánimo de lucro, ofrecido de forma gratuita y exclusiva a la Asociación del Oriente Colombiano.",
    },
    {
      icon: Zap,
      title: "Siempre disponible",
      description:
        "Funciona en cualquier dispositivo con internet: computador, tablet o celular. Los cambios se ven al instante en todos lados.",
    },
  ];

  return (
    <section className="py-20 md:py-32 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-100 text-emerald-700 text-sm font-medium mb-4">
            <Shield className="w-4 h-4" />
            Hecho con cuidado
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
            Una herramienta segura, gratuita y al servicio de la iglesia
          </h2>
          <p className="mt-4 text-lg text-gray-600">
            Sabemos lo importante que es la información de las familias del
            club. Por eso Insignia se construyó cuidando la seguridad, la
            privacidad y la sencillez de uso.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
          {pillars.map((p, i) => {
            const Icon = p.icon;
            return (
              <div
                key={i}
                className="bg-white rounded-2xl p-6 border border-gray-100 hover:border-primary-200 hover:shadow-lg transition-all duration-300 flex gap-4"
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center flex-shrink-0 shadow-md">
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">
                    {p.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {p.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ============================================
// CTA SECTION
// ============================================
function CTASection() {
  return (
    <section className="py-20 md:py-32 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-primary-500 to-accent-500 mb-6 shadow-lg shadow-primary-500/30">
          <img src="/branding/insignia_icon.svg" alt="" className="w-10 h-10" />
        </div>
        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
          Comienza a gestionar tu club hoy
        </h2>
        <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
          Accede al portal con tu cuenta o regístrate para que el director de tu
          club o el administrador de la asociación active tus permisos.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/login">
            <Button
              size="xl"
              className="w-full sm:w-auto shadow-lg shadow-primary-500/30"
            >
              Iniciar sesión
              <ArrowRight className="w-5 h-5" />
            </Button>
          </Link>
          <Link to="/register">
            <Button variant="secondary" size="xl" className="w-full sm:w-auto">
              Crear cuenta
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}

// ============================================
// FOOTER
// ============================================
function Footer() {
  return (
    <footer className="bg-gray-900 text-gray-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <img
                src="/branding/insignia_icon.svg"
                alt="Insignia"
                className="w-10 h-10"
              />
              <div>
                <span className="text-xl font-bold text-white">Insignia</span>
                <p className="text-sm text-gray-500">
                  Gestión de Clubes Juveniles
                </p>
              </div>
            </div>
            <p className="text-sm text-gray-400 leading-relaxed">
              Plataforma oficial de uso exclusivo para la{" "}
              <strong className="text-gray-200">
                Asociación del Oriente Colombiano
              </strong>{" "}
              de la Iglesia Adventista del Séptimo Día.
            </p>
          </div>

          {/* Plataforma */}
          <div>
            <h3 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">
              Plataforma
            </h3>
            <ul className="space-y-2 text-sm">
              <li>
                <Link
                  to="/login"
                  className="hover:text-white transition-colors"
                >
                  Iniciar sesión
                </Link>
              </li>
              <li>
                <Link
                  to="/register"
                  className="hover:text-white transition-colors"
                >
                  Crear cuenta
                </Link>
              </li>
              <li>
                <Link
                  to="/forgot-password"
                  className="hover:text-white transition-colors"
                >
                  Recuperar contraseña
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">
              Legal
            </h3>
            <ul className="space-y-2 text-sm">
              <li>
                <Link
                  to="/terms"
                  className="hover:text-white transition-colors"
                >
                  Términos y Condiciones
                </Link>
              </li>
              <li>
                <Link
                  to="/privacy"
                  className="hover:text-white transition-colors"
                >
                  Política de Privacidad
                </Link>
              </li>
              <li className="text-gray-500 text-xs pt-2">
                Cumplimiento Ley 1581/2012 (Habeas Data · Colombia)
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-12 pt-8 border-t border-gray-800 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-gray-500">
          <p>
            © {new Date().getFullYear()} Insignia · Proyecto sin ánimo de lucro
          </p>
          <PoweredByDLSoft theme="dark" variant="badge" size="sm" />
        </div>
      </div>
    </footer>
  );
}

// ============================================
// MAIN LANDING PAGE
// ============================================
export default function LandingPage() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main>
        <HeroSection />
        <ProblemSection />
        <SolutionSection />
        <WebFeaturesSection />
        <MobileFeaturesSection />
        <ArchitectureSection />
        <TrustSection />
        <CTASection />
      </main>
      <Footer />
    </div>
  );
}
